import { useState, useEffect, useCallback } from 'react';
import { companyAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

export interface FeedingSchedule {
  id?: number;
  time: string;
  ratio: string;
  notes: string;
}

export interface Starter {
  id: number;
  name: string;
  consistency: string;
  consistency_display: string;
  ready_by_time: string | null;
  maintenance_ratio: string;
  flour_source: number | null;
  flour_source_name: string;
  notes: string;
  feeding_schedules: FeedingSchedule[];
  created_at: string;
  updated_at: string;
}

const emptyForm = {
  name: '',
  consistency: 'liquid',
  ready_by_time: '',
  maintenance_ratio: '',
  flour_source: '',
  notes: '',
};

export type StarterForm = typeof emptyForm;

const emptySchedule = (): FeedingSchedule => ({ time: '', ratio: '', notes: '' });

export function useCompanyStarters(flourIngredients: any[]) {
  const { isExpired } = useAuth();
  const [starters, setStarters] = useState<Starter[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [feedback, setFeedback] = useState({ error: '', success: '' });

  const [showModal, setShowModal] = useState(false);
  const [editingStarter, setEditingStarter] = useState<Starter | null>(null);
  const [form, setForm] = useState<StarterForm>(emptyForm);
  const [schedules, setSchedules] = useState<FeedingSchedule[]>([]);
  const [saving, setSaving] = useState(false);

  const [deleteState, setDeleteState] = useState<{
    showModal: boolean; starter: Starter | null; deleting: boolean;
  }>({ showModal: false, starter: null, deleting: false });

  const fetchStarters = useCallback(async () => {
    try {
      const res = await companyAPI.getStarters();
      setStarters(res.data);
    } catch (err) {
      console.error('Failed to fetch starters:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStarters(); }, [fetchStarters]);

  const filtered = starters.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenCreate = () => {
    setEditingStarter(null);
    setForm(emptyForm);
    setSchedules([]);
    setFeedback({ error: '', success: '' });
    setShowModal(true);
  };

  const handleOpenEdit = (starter: Starter) => {
    setEditingStarter(starter);
    setForm({
      name: starter.name,
      consistency: starter.consistency,
      ready_by_time: starter.ready_by_time ?? '',
      maintenance_ratio: starter.maintenance_ratio,
      flour_source: starter.flour_source ? String(starter.flour_source) : '',
      notes: starter.notes,
    });
    setSchedules(starter.feeding_schedules.map(s => ({ ...s })));
    setFeedback({ error: '', success: '' });
    setShowModal(true);
  };

  const addSchedule = () => setSchedules(prev => [...prev, emptySchedule()]);
  const removeSchedule = (idx: number) => setSchedules(prev => prev.filter((_, i) => i !== idx));
  const updateSchedule = (idx: number, field: keyof FeedingSchedule, value: string) => {
    setSchedules(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setFeedback({ error: 'Name is required.', success: '' });
      return;
    }
    setSaving(true);
    setFeedback({ error: '', success: '' });
    const payload: any = {
      name: form.name.trim(),
      consistency: form.consistency,
      ready_by_time: form.ready_by_time || null,
      maintenance_ratio: form.maintenance_ratio,
      flour_source: form.flour_source ? parseInt(form.flour_source) : null,
      notes: form.notes,
      feeding_schedules: schedules.map(s => ({
        time: s.time || null,
        ratio: s.ratio,
        notes: s.notes,
      })),
    };
    try {
      if (editingStarter) {
        const res = await companyAPI.updateStarter(editingStarter.id, payload);
        setStarters(prev => prev.map(s => s.id === editingStarter.id ? res.data : s));
      } else {
        const res = await companyAPI.createStarter(payload);
        setStarters(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      }
      setShowModal(false);
      setFeedback({ error: '', success: editingStarter ? 'Starter updated.' : 'Starter created.' });
    } catch (err: any) {
      const msg = err?.response?.data?.name?.[0] || err?.response?.data?.detail || 'Failed to save starter.';
      setFeedback({ error: msg, success: '' });
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = (starter: Starter) => {
    setDeleteState({ showModal: true, starter, deleting: false });
  };

  const handleDelete = async () => {
    if (!deleteState.starter) return;
    setDeleteState(prev => ({ ...prev, deleting: true }));
    try {
      await companyAPI.deleteStarter(deleteState.starter.id);
      setStarters(prev => prev.filter(s => s.id !== deleteState.starter!.id));
      setDeleteState({ showModal: false, starter: null, deleting: false });
      setFeedback({ error: '', success: 'Starter deleted.' });
    } catch {
      setDeleteState(prev => ({ ...prev, deleting: false }));
      setFeedback({ error: 'Failed to delete starter.', success: '' });
    }
  };

  return {
    isExpired,
    starters,
    loading,
    search,
    setSearch,
    filtered,
    showModal,
    editingStarter,
    form,
    setForm,
    schedules,
    saving,
    feedback,
    setFeedback,
    deleteState,
    handleOpenCreate,
    handleOpenEdit,
    addSchedule,
    removeSchedule,
    updateSchedule,
    handleSave,
    handleConfirmDelete,
    handleDelete,
    closeModal: () => setShowModal(false),
    closeDeleteModal: () => setDeleteState({ showModal: false, starter: null, deleting: false }),
  };
}
